import { z } from 'zod';
import { sanitizeInput } from '../utils/sanitizer';

/**
 * Frontend Preference Validation Constants mirroring backend constraints.
 * All bounds are defined as named constants to avoid magic values.
 */
export const PREFERENCE_VALIDATION_CONSTANTS = {
  MAX_UI_SETTINGS_ENTRIES: 50,
  MAX_UI_SETTING_KEY_LENGTH: 64,
  MAX_UI_SETTING_VALUE_LENGTH: 256,
} as const;

/**
 * Frontend validation schema for updating user preferences.
 * Includes prototype pollution defense and UI-level XSS sanitization.
 */
export const updateUserPreferenceSchema = z.object({
  themeMode: z.enum(['light', 'dark', 'system']).optional(),
  uiSettings: z.record(
    z.string()
      .min(1)
      .max(PREFERENCE_VALIDATION_CONSTANTS.MAX_UI_SETTING_KEY_LENGTH)
      .refine(key => !['__proto__', 'constructor', 'prototype'].includes(key), {
        message: 'Prototype pollution attempt detected'
      })
      .transform(val => sanitizeInput(val)),
    z.union([
      z.string()
        .max(PREFERENCE_VALIDATION_CONSTANTS.MAX_UI_SETTING_VALUE_LENGTH)
        .transform(val => sanitizeInput(val)),
      z.boolean(),
      z.number().finite(),
      z.record(
        z.union([
          z.string()
            .max(PREFERENCE_VALIDATION_CONSTANTS.MAX_UI_SETTING_VALUE_LENGTH)
            .transform(val => sanitizeInput(val)),
          z.boolean(),
          z.number().finite(),
        ])
      ),
    ])
  ).refine(
    (val) => Object.keys(val).length <= PREFERENCE_VALIDATION_CONSTANTS.MAX_UI_SETTINGS_ENTRIES,
    { message: `uiSettings entries cannot exceed ${PREFERENCE_VALIDATION_CONSTANTS.MAX_UI_SETTINGS_ENTRIES}` }
  ).optional().default({}),
}).strict();

export type UpdateUserPreferenceInputs = z.infer<typeof updateUserPreferenceSchema>;
